import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "./PageHero";
import { Icon } from "./ui";
import { smoothScrollToElement } from "./smoothScroll";

type LegalPageProps = {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
};

type TocEntry = { id: string; label: string };

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/^\d+\.\s*/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Typography for plain h2 / p / a content inside legal documents
const proseClass =
  "[&_h2]:font-headline-sm [&_h2]:text-headline-sm [&_h2]:text-on-surface [&_h2]:mt-10 [&_h2]:mb-3 [&_h2:first-child]:mt-0 [&_p]:font-body-md [&_p]:text-body-md [&_p]:text-on-surface-variant [&_p]:mb-4 [&_a]:text-primary [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-words";

export const LegalPage: FC<LegalPageProps> = ({
  eyebrow,
  title,
  description,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [toc, setToc] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState("");

  // Build the table of contents from the document's section headings
  useEffect(() => {
    const headings = Array.from(
      contentRef.current?.querySelectorAll("h2") ?? [],
    );
    headings.forEach((h) => {
      h.id = h.id || slugify(h.textContent ?? "");
    });
    setToc(headings.map((h) => ({ id: h.id, label: h.textContent ?? "" })));

    // Highlight the section currently being read
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-96px 0px -70% 0px" },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, []);

  const goTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) smoothScrollToElement(el);
  };

  const tocLinks = (onPick?: () => void) =>
    toc.map((entry) => (
      <li key={entry.id}>
        <button
          type="button"
          onClick={() => {
            goTo(entry.id);
            onPick?.();
          }}
          className={`w-full text-left px-3 py-2 rounded-lg font-body-sm text-body-sm transition-colors ${
            activeId === entry.id
              ? "bg-primary-fixed/50 text-primary font-semibold"
              : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low"
          }`}
        >
          {entry.label}
        </button>
      </li>
    ));

  return (
    <main>
      <PageHero eyebrow={eyebrow} title={title} description={description} />
      <section className="w-full pb-20 bg-surface-container-low/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Table of contents */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28">
            {/* Phones / tablets: collapsible */}
            <details className="lg:hidden group bg-surface-container-lowest rounded-2xl shadow-sm">
              <summary className="flex items-center justify-between gap-3 p-4 cursor-pointer list-none font-label-lg text-label-lg text-on-surface [&::-webkit-details-marker]:hidden">
                <span className="flex items-center gap-2">
                  <Icon name="toc" className="text-primary" />
                  On this page
                </span>
                <Icon
                  name="expand_more"
                  className="text-on-surface-variant transition-transform group-open:rotate-180"
                />
              </summary>
              <ul className="list-none m-0 px-2 pb-3 flex flex-col gap-0.5">
                {tocLinks()}
              </ul>
            </details>
            {/* Desktop: sticky sidebar */}
            <nav
              aria-label="On this page"
              className="hidden lg:flex flex-col gap-3 bg-surface-container-lowest rounded-2xl shadow-sm p-4 max-h-[calc(100vh-9rem)] overflow-y-auto"
            >
              <span className="px-3 pt-1 font-label-sm text-label-sm uppercase tracking-wider text-primary">
                On this page
              </span>
              <ul className="list-none m-0 p-0 flex flex-col gap-0.5">
                {tocLinks()}
              </ul>
            </nav>
          </aside>

          {/* Document */}
          <article className="lg:col-span-8 bg-surface-container-lowest rounded-3xl shadow-xl p-6 sm:p-10">
            <div ref={contentRef} className={proseClass}>
              {children}
            </div>
            <div className="mt-10 p-5 rounded-2xl bg-surface-container-low flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                Still have questions about this policy?
              </p>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-on-primary font-label-md text-label-md hover:bg-primary-container transition-colors"
              >
                Contact Us
                <Icon name="arrow_forward" className="text-base" />
              </Link>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
};
